/**
 * Customer Pilot API service (kanban 63b736fe).
 *
 * The one-customer Merlin, reached through the profile-LOCKED door at
 * `/api/customer-pilot` (the backend forces the `customer_pilot` profile and
 * fixes the customer from its settings; nothing about WHICH customer is sent
 * from here). TEXT-only client over the shared Agent Console SSE wire contract,
 * like analystCopilot.ts, plus one extra read: `getPilotScope`, the customer
 * the door is scoped to, for the page banner.
 */
import api from './api';
import {
  CONSOLE_DOOR_REQUEST_TIMEOUT_MS,
  streamConsoleSse,
  type ConsoleEvent,
  type ConversationCreate,
  type ConversationDetail,
  type ConversationRead,
  type ModelsResponse,
} from './agentConsole';

export type {
  ConsoleEvent,
  ConversationCreate,
  ConversationDetail,
  ConversationRead,
  ModelOption,
  ModelsResponse,
} from './agentConsole';

/** The door's API prefix (matches app/routes/customer_pilot_chat.py). */
export const CUSTOMER_PILOT_BASE = '/customer-pilot';

/** The customer this door reads (GET /customer-pilot/scope). */
export interface PilotScope {
  customer_no: string;
  label: string;
  email_domains: string[];
}

export async function getPilotScope(): Promise<PilotScope> {
  const { data } = await api.get<PilotScope>(`${CUSTOMER_PILOT_BASE}/scope`);
  return data;
}

export async function createConversation(
  body: ConversationCreate = {},
): Promise<ConversationRead> {
  const { data } = await api.post<ConversationRead>(
    `${CUSTOMER_PILOT_BASE}/conversations`,
    body,
    { timeout: CONSOLE_DOOR_REQUEST_TIMEOUT_MS },
  );
  return data;
}

/** Selectable models + this door's default model, for the picker. */
export async function getModels(): Promise<ModelsResponse> {
  const { data } = await api.get<ModelsResponse>(`${CUSTOMER_PILOT_BASE}/models`);
  return data;
}

export async function listConversations(
  opts: { limit?: number; offset?: number } = {},
): Promise<ConversationRead[]> {
  const params: Record<string, number> = {};
  if (opts.limit != null) params.limit = opts.limit;
  if (opts.offset != null) params.offset = opts.offset;
  const { data } = await api.get<{ items: ConversationRead[] }>(
    `${CUSTOMER_PILOT_BASE}/conversations`,
    { params },
  );
  return data.items;
}

export async function getConversationDetail(
  conversationId: string,
): Promise<ConversationDetail> {
  const { data } = await api.get<ConversationDetail>(
    `${CUSTOMER_PILOT_BASE}/conversations/${conversationId}`,
    { timeout: CONSOLE_DOOR_REQUEST_TIMEOUT_MS },
  );
  return data;
}

/**
 * Open the SSE stream for one pilot turn. The profile (and the customer) are
 * forced server-side, so only the prompt and the optional model are sent.
 */
export async function streamCustomerPilotMessage(
  conversationId: string,
  prompt: string,
  onEvent: (evt: ConsoleEvent) => void,
  signal?: AbortSignal,
  model?: string,
): Promise<void> {
  const payload: { prompt: string; model?: string } = { prompt };
  if (model) payload.model = model;
  return streamConsoleSse(
    `/api${CUSTOMER_PILOT_BASE}/conversations/${conversationId}/message`,
    payload,
    onEvent,
    signal,
  );
}
